import axios, { type AxiosError } from "axios";

import { logger } from "@/utils/logger";

import { getPublicApiBaseUrl } from "../utils/publicRuntimeConfig";

export const axiosInstance = axios.create({
  baseURL: getPublicApiBaseUrl() ?? process.env.REACT_APP_BASE_URL ?? "",
  // timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Flag to prevent multiple simultaneous redirects
let isRedirecting = false;

/**
 * Check if an endpoint is authentication-related and should be excluded from session timeout handling
 */
const isAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;

  const authEndpoints = [
    "/api/v1/auth/localLogin",
    "/api/v1/auth/check",
    "/api/v1/auth/logout",
    "/api/v1/auth/register",
    "/api/v1/auth/microsoft/login",
    "/api/v1/auth/microsoft/callback",
    "/api/v1/auth/accept-privacy",
    "/api/v1/auth/verify-2fa",
    "/api/v1/auth/forgot-password",
    "/api/v1/auth/verify-reset-token",
    "/api/v1/auth/reset-password",
    "/api/v1/auth/verify-invite-token",
    "/api/v1/auth/accept-invite",
    "/api/v1/auth/change-password",
  ];

  return authEndpoints.some((endpoint) => url.includes(endpoint));
};

const isPublicAuthPage = (path: string): boolean => {
  return (
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    path.startsWith("/set-password")
  );
};

/**
 * Handle session timeout by clearing cookies and redirecting to login
 */
const handleSessionTimeout = (): void => {
  // Prevent multiple simultaneous redirects
  if (isRedirecting) {
    return;
  }

  // Only redirect if not already on login page or public auth pages
  const currentPath = window.location.pathname;
  if (currentPath === "/login" || currentPath === "/" || currentPath === "/2fa-verify" || isPublicAuthPage(currentPath)) {
    return;
  }

  isRedirecting = true;

  // Clear all cookies
  document.cookie.split(";").forEach(function (c) {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, `=;expires=${  new Date().toUTCString()  };path=/`);
  });

  logger.info("Session expired, redirecting to login", {
    currentPath,
    context: "handleSessionTimeout",
  });

  // Use window.location.href for a full page reload to ensure clean state
  window.location.href = "/login";
};

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Log API requests in development
    logger.debug("API request initiated", {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      context: "axiosRequest",
    });
    return config;
  },
  (error) => {
    logger.error("API request configuration error", {
      error,
      context: "axiosRequestInterceptor",
    });
    return Promise.reject(error);
  }
);

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Check for session expiration in successful responses
    // Backend may return sessionExpired: true even in 200 responses
    const data = response.data;
    if (data && typeof data === "object" && data.sessionExpired === true) {
      // Don't redirect if we're already on login or auth-related pages
      const currentPath = window.location.pathname;
      const isOnLoginPage = currentPath === "/login" || currentPath.startsWith("/login");
      const isOnAuthPage = currentPath === "/2fa-verify" || currentPath.startsWith("/2fa-verify");

      if (isOnLoginPage || isOnAuthPage || isPublicAuthPage(currentPath)) {
        // Already on login/public auth page, just clear session storage and return error
        // We intentionally avoid clearing localStorage here so that user
        // preferences (e.g., Netskope Availability widget config) persist
        // across session expirations.
        sessionStorage.clear();
        logger.warn("Session expired (already on login/public auth page)", {
          message: data.message || "Session expired",
          currentPath,
          context: "axiosResponseInterceptor",
        });
        return Promise.reject(new Error("Session expired"));
      }

      // Clear any session storage data; keep localStorage (user preferences) intact
      sessionStorage.clear();

      // Log session expiration
      logger.warn("Session expired", {
        message: data.message || "Session expired",
        redirectTo: data.redirectTo || "/login",
        context: "axiosResponseInterceptor",
      });

      // Show notification (optional - uncomment if you have a toast system)
      // toast.error(data.message || 'Your session has expired. Please login again.');

      // Redirect to login
      window.location.href = data.redirectTo || "/login";
      return Promise.reject(new Error("Session expired"));
    }

    // Log successful API responses
    logger.debug("API request completed", {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      context: "axiosResponse",
    });
    return response;
  },
  (error: AxiosError | any) => {
    // Handle 401 errors and check for session expiration
    // Backend returns sessionExpired: true when session expires due to inactivity
    if (error.response?.status === 401) {
      const data = error.response.data;

      // Check if session expired due to inactivity
      // Distinguish between:
      // - sessionExpired: true → redirect to login (activity-based expiration)
      // - sessionExpired: false or missing → normal unauthorized error (invalid credentials, etc.)
      if (data && typeof data === "object" && data.sessionExpired === true) {
        // Don't redirect if we're already on login or auth-related pages
        const currentPath = window.location.pathname;
        const isOnLoginPage = currentPath === "/login" || currentPath.startsWith("/login");
        const isOnAuthPage = currentPath === "/2fa-verify" || currentPath.startsWith("/2fa-verify");

        // Also check if this is a login/auth endpoint request
        const requestUrl = error.config?.url || "";
        const isLoginEndpoint =
          requestUrl.includes("/auth/login") ||
          requestUrl.includes("/auth/localLogin") ||
          requestUrl.includes("/auth/microsoft");

        if (isOnLoginPage || isOnAuthPage || isLoginEndpoint || isPublicAuthPage(currentPath)) {
          // Already on login/public auth page or making login request, just clear session storage and return error
          // Keep localStorage so non-sensitive preferences survive a failed auth attempt.
          sessionStorage.clear();
          logger.warn("Session expired (already on login/auth page)", {
            message: data.message || "Session expired",
            currentPath,
            requestUrl,
            context: "axiosResponseInterceptor",
          });
          return Promise.reject(new Error("Session expired"));
        }

        // Clear any session storage data; keep localStorage (user preferences) intact
        sessionStorage.clear();

        // Log session expiration
        logger.warn("Session expired due to inactivity", {
          message: data.message || "Session expired due to inactivity. Please login again.",
          redirectTo: data.redirectTo || "/login",
          context: "axiosResponseInterceptor",
        });

        // Show notification (optional - uncomment if you have a toast system)
        // toast.error(data.message || 'Your session has expired due to inactivity. Please login again.');

        // Redirect to login
        // Note: Activity-based expiration - any authenticated API call resets the idle timer
        // No frontend changes needed for activity tracking; backend handles it automatically
        window.location.href = data.redirectTo || "/login";
        return Promise.reject(new Error("Session expired"));
      }
      // If sessionExpired is false or missing, treat as normal unauthorized error
      // (e.g., invalid credentials, insufficient permissions)
    }

    const status = error.response?.status;
    const requestUrl = error.config?.url;

    // Log API errors
    logger.error("API request failed", {
      error,
      method: error.config?.method?.toUpperCase(),
      url: requestUrl,
      status,
      statusText: error.response?.statusText,
      context: "axiosResponseInterceptor",
    });

    // Handle session timeout (401 Unauthorized) for non-auth endpoints
    if (status === 401 && !isAuthEndpoint(requestUrl)) {
      logger.warn("Session expired or invalid token detected", {
        url: requestUrl,
        status,
        context: "sessionTimeout",
      });

      // Handle session timeout
      handleSessionTimeout();
    }

    return Promise.reject(error);
  }
);
